'use strict';
import $ from 'jquery';
import Mustache from 'mustache';
import {openModal} from '../ui/modal';
import Promise from 'bluebird';

/* global ApplePaySession */

export default class ApplePay {
	/**
	 * @param {object} order
	 * @param {string} order.orderId - order ID, visible to customer
	 * @param {string} order.total - order total amount
	 * @param {string} [order.countryCode=FI]
	 * @param {string} [order.currencyCode=EUR]
	 * @param {object} options
	 * @param {string} options.merchantId - Apple Pay merchant ID
	 * @param {string} options.applePayUrl - applepay_url from Stage payment component
	 * @param {string} options.paymentUrl - applepay_payment_url from Stage payment component
	 * @param {string} options.labelPrefix - This is displayed on payment prompt followed by order number
	 * @param {string[]} options.supportedNetworks - The payment netorks supported by the merchant, e.g. visa, masterCard
	 * @param {string[]} [options.merchantCapabilities=['supports3DS']] - The payment capabilities supported by the merchant.
	 * @param {number} [options.applePayVersion=3] - Apple Pay version
	 * @param {boolean} [options.devMode=false] - Debug output s displayed when devMode is `true`
	 */
	constructor(order, options) {
		const {
			merchantId,
			applePayUrl,
			paymentUrl,
			labelPrefix,
			supportedNetworks,
			merchantCapabilities = ['supports3DS'],
			applePayVersion = 3,
			devMode = false
		} = options;
		this.order = order;
		this.merchantId = merchantId;
		this.applePayUrl = applePayUrl;
		this.paymentUrl = paymentUrl;
		this.labelPrefix = labelPrefix;
		this.applePayVersion = applePayVersion;
		this.supportedNetworks = supportedNetworks;
		this.merchantCapabilities = merchantCapabilities;
		this.devMode = devMode;
		this.errorHandler = this.handleError.bind(this);
		this.initialized = false;
		this.isEnabled = false;
	}

	setOrder(order) {
		this.order = order;
	}

	/**
	 * Check if Apple Pay is available and user can make payments with active card.
	 * 
	 * @returns {Promise<boolean>}
	 */
	async canMakePayments() {
		if (this.initialized) {
			return this.isEnabled;
		}
		if (!window.ApplePaySession) {
			return false;
		}
		const canMakePayments = await ApplePaySession.canMakePaymentsWithActiveCard(this.merchantId);
		this.isEnabled = canMakePayments;
		this.initialized = true;
		return canMakePayments;
	}

	/**
	 * Display Apple Pay payment modal.
	 * 
	 * @param {HTMLElement} button 
	 */
	async openModal(button) {
		const isApplePayEnabled = this.devMode ? Promise.resolve(true) : await this.canMakePayments();
		const modalTemplate = (document.querySelector('#mustache-applepay') || {}).innerHTML || '';
		const modalContent = Mustache.render(modalTemplate, {isApplePayEnabled});
		openModal(button, {content: $(modalContent)});
		if (isApplePayEnabled) {
			this.messageContainer = document.getElementById('apaymsg');
			const applePayButton = document.getElementById('apay');
			this.debug(this);
			if (applePayButton) {
				applePayButton.addEventListener('click', () => this.startSession());
			}
		}
	}
	
	/**
	 * @param {Error} err 
	 */
	handleError(err) {
		console.error(err);
		this.debug('Error: ' + err.message);
	}

	/**
	 * @param {string|object} message 
	 */
	debug(message) {
		if (this.devMode) {
			const msg = typeof message === 'string' ? message : JSON.stringify(message, null, 2);
			this.messageContainer.innerHTML = msg;
		}
	}

	startSession() {
		if (!this.order || !this.order.orderId || !this.order.total) {
			throw new Error('ApplePay#startSession: Missing or invalid order object');
		}
		const {countryCode = 'FI', currencyCode = 'EUR'} = this.order;
		const request = {
			supportedNetworks: this.supportedNetworks,
			merchantCapabilities: this.merchantCapabilities,
			countryCode,
			currencyCode,
			total: {
				amount: this.order.total,
				label: this.labelPrefix + this.order.orderId
			}
		};
		try {
			this.createSession(request);
		} catch (e) {
			this.handleError(e);
		}
	}

	/**
	 * 
	 * @param {object} request 
	 */
	createSession(request) {
		this.session = new ApplePaySession(this.applePayVersion, request);
		this.session.onvalidatemerchant = e => this.validateMerchant(e).catch(this.errorHandler);
		this.session.onpaymentauthorized = e => this.completePayment(e).catch(this.errorHandler);
		this.session.begin();
	}

	/**
   * @param {Event} event 
   */
	async validateMerchant(event) {
		const url = `${this.applePayUrl}?validationURL=${encodeURIComponent(event.validationURL)}`;
		this.debug(url);
		const response = await fetch(url);
		const data = await response.json();
		this.debug(data);
		this.session.completeMerchantValidation(data);
	}

	/**
   * @param {Event} event 
   */
	async completePayment(event) {
		const formData = new FormData();
		formData.append('paymentdata', JSON.stringify(event.payment.token));
		formData.append('orderid', this.order.orderId);
		this.debug(this.paymentUrl);
		const response = await fetch(this.paymentUrl, {
			method: 'POST',
			body: formData
		});
		const data = await response.json();
		this.debug(data);
		const status = data.status ? ApplePaySession.STATUS_SUCCESS : ApplePaySession.STATUS_FAILURE;
		this.session.completePayment(status);
		if (data.redirect) {
			window.location.href = data.redirect;
		}
	}
}
